// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import siteMessage from "@insite/client-framework/SiteMessage";
import setFlyoutDrawerIsOpen from "@insite/client-framework/Store/Components/AddressDrawer/Handlers/SetFlyoutDrawerIsOpen";
import setNavDrawerIsOpen from "@insite/client-framework/Store/Components/AddressDrawer/Handlers/SetNavDrawerIsOpen";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import loadCurrentCart from "@insite/client-framework/Store/Data/Carts/Handlers/LoadCurrentCart";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import List from "@insite/mobius/Icons/List";
import ShoppingBag from "@insite/mobius/Icons/ShoppingBag";
import Truck from "@insite/mobius/Icons/Truck";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import { FloatingTooltip, FloatingTooltipStyles } from "@keller/App/Components/FloatingTooltip/FloatingTooltip";
import HeaderClickable from "@keller/App/Components/Header/HeaderClickable";
import MiniCart from "@keller/App/Components/MiniCart/MiniCart";
import { CustomAppState } from "@keller/App/Store/Reducers";
import { getIsNonPurchasable } from "@keller/App/Store/Selectors/GeneralSelectors";
import Cart from "@keller/XDL/Icons/Cart";
import React, { FC, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, withTheme } from "styled-components";

const enum fields {
    visibilityState = "visibilityState",
    icon = "icon",
    showInWebOnly = "showInWebOnly",
}

const mapStateToProps = (state: CustomAppState) => ({
    shouldLoadCart: !getCurrentCartState(state).value && !getCurrentCartState(state).isLoading,
    totalCountDisplay: getCurrentCartState(state).value?.totalCountDisplay,
    cartUrl: getPageLinkByPageType(state, "CartPage")?.url,
    // XNG-Changes
    orderSubTotalDisplay: getCurrentCartState(state).value?.orderSubTotalDisplay,
    currencySymbol:
        getCurrentCartState(state).value?.currencySymbol || state.context.session.currency?.currencySymbol || "$",
    isTabletScreen: state.xCommon.isTabletScreen,
    isCartPage: getCurrentPage(state).type === "CartPage",
    isCheckoutPage:
        getCurrentPage(state).type === "CheckoutShippingPage" ||
        getCurrentPage(state).type === "CheckoutReviewAndSubmitPage",
    isNonPurchasable: getIsNonPurchasable(state),
    MyListUrl: getPageLinkByPageType(state, "MyListsPage")?.url,
    isSticky: state.xHeader.isSticky,
});

const mapDispatchToProps = {
    loadCurrentCart,
    setNavDrawerIsOpen,
    setFlyoutDrawerIsOpen,
};

export interface CartLinkStyles {
    routerLink?: LinkPresentationProps;
    desktopCountText?: TypographyPresentationProps;
    mobileCountText?: TypographyPresentationProps;
    compactCountText?: TypographyPresentationProps;
    floatingTooltip?: FloatingTooltipStyles;
}

export const cartLinkStyles: CartLinkStyles = {
    routerLink: {
        typographyProps: {
            variant: "headerSecondary",
        },
        icon: {
            iconProps: {
                size: 24,
            },
        },
        color: "secondary.contrast",
        css: css`
            height: 50px;
            padding: 0 4px 0 10px;
        `,
    },
    compactCountText: {
        css: css`
            font-weight: normal;
        `,
    },
};

const iconMap = {
    cart: Cart,
    truck: Truck,
    bag: ShoppingBag,
    none: undefined,
} as const;

interface OwnProps extends WidgetProps {
    fields: {
        [fields.visibilityState]: "both" | "label" | "icon";
        [fields.icon]: keyof typeof iconMap;
        [fields.showInWebOnly]: boolean;
    };
    isCompactHeaderCart?: boolean;
    theme: BaseTheme;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

/** Reloading the cart makes `totalCountDisplay` temporarily unavailable, so retain the previous value for better UX. */
let retainedTotalCountToDisplay: number | undefined;

const CartLink: FC<Props> = ({
    shouldLoadCart,
    cartUrl,
    totalCountDisplay,
    fields: { visibilityState, icon: selectedIcon, showInWebOnly },
    loadCurrentCart,
    isCompactHeaderCart,
    theme,
    currencySymbol,
    orderSubTotalDisplay,
    isTabletScreen,
    setNavDrawerIsOpen,
    setFlyoutDrawerIsOpen,
    isCheckoutPage,
    isNonPurchasable,
    MyListUrl,
    isSticky,
    isCartPage,
}) => {
    const history = useHistory();
    // XNG-Changes
    const [isMenuOpen, setMenuOpen] = useState(false);

    useEffect(() => {
        if (shouldLoadCart) {
            loadCurrentCart({ shouldLoadFullCart: false, replaceCart: false });
        }
    }, [shouldLoadCart]);

    const styles = useMergeStyles("cartLink", cartLinkStyles);

    const showIcon = visibilityState !== "label";
    const showLabel = visibilityState !== "icon";

    if (typeof totalCountDisplay !== "undefined") {
        retainedTotalCountToDisplay = totalCountDisplay || 0;
    } else if (typeof totalCountDisplay === "undefined") {
        retainedTotalCountToDisplay = 0;
    }

    const icon = iconMap[(showIcon && selectedIcon) || "none"];

    const { routerLink } = styles;

    // XNG-Changes
    const openCartPageHandler = () => {
        if (!cartUrl) {
            return;
        }
        history.push(cartUrl);
    };
    const openMYListPageHandler = () => {
        if (!MyListUrl) {
            return;
        }
        history.push(MyListUrl);
    };

    if (isCompactHeaderCart) {
        return (
            <Link
                {...routerLink}
                color={theme.header.linkColor}
                href={cartUrl}
                icon={{ ...routerLink?.icon, iconProps: { src: icon, ...routerLink?.icon?.iconProps } }}
                data-test-selector="cartLink"
            >
                <Typography as="span" {...styles.compactCountText}>
                    {retainedTotalCountToDisplay}
                </Typography>
                <VisuallyHidden>{siteMessage("Header_CartCount")}</VisuallyHidden>
            </Link>
        );
    }

    const closeNavigationDrawer = () => {
        setFlyoutDrawerIsOpen({ isOpen: false });
        setTimeout(() => setFlyoutDrawerIsOpen({ isOpen: undefined }), 300);
        setNavDrawerIsOpen({ navDrawerIsOpen: false });
        setTimeout(() => setNavDrawerIsOpen({ navDrawerIsOpen: undefined }), 300);
    };

    const headerClickableElement = (
        <HeaderClickable
            isPopupShown={isMenuOpen}
            onClick={!isNonPurchasable ? openCartPageHandler : openMYListPageHandler}
            iconProps={!isNonPurchasable ? { src: icon } : { src: List }}
            data-test-selector="cartLink"
            numberOfCartItems={retainedTotalCountToDisplay}
        >
            <>
                {isNonPurchasable ? (
                    translate("My Lists")
                ) : (
                    <>
                        {`${translate("Cart")} (${retainedTotalCountToDisplay ?? 0})`}
                        <Typography className="sub-title">{`${
                            orderSubTotalDisplay ?? `${currencySymbol}0.00`
                        }`}</Typography>
                    </>
                )}
            </>
        </HeaderClickable>
    );

    // XNG-Changes : show mini cart in header based on web-only users and showInWebOnly flag in cms
    const showCart = isNonPurchasable ? showInWebOnly : true;

    // Stop MiniCart in Checkout pages and web-only users
    if (isCheckoutPage || isNonPurchasable || isCartPage) {
        return headerClickableElement;
    }

    return (
        <>
            {showCart && (
                <FloatingTooltip
                    onOpenChange={open => setMenuOpen(open)}
                    showArrow={!isTabletScreen}
                    trigger="hover"
                    enabled={!isTabletScreen}
                    offset={{ mainAxis: isSticky ? 24 : 20, crossAxis: 0 }}
                    placement="bottom-end"
                    triggerComponent={headerClickableElement}
                    extendedStyles={styles.floatingTooltip}
                    overlay={true}
                    shift={{ padding: 0 }}
                >
                    <MiniCart isPopupShown={isMenuOpen} />
                </FloatingTooltip>
            )}
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withTheme(CartLink)),
    definition: {
        displayName: "Cart",
        icon: "cart-shopping",
        fieldDefinitions: [
            {
                name: fields.visibilityState,
                displayName: "Settings",
                editorTemplate: "RadioButtonsField",
                options: [
                    {
                        displayName: "Show Both",
                        value: "both",
                    },
                    {
                        displayName: "Show Label Only",
                        value: "label",
                    },
                    {
                        displayName: "Show Icon Only",
                        value: "icon",
                    },
                ],
                defaultValue: "both",
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.icon,
                displayName: "Shopping Cart Icon",
                editorTemplate: "DropDownField",
                options: [
                    {
                        displayName: "None",
                        value: "none",
                    },
                    {
                        displayName: "Bag",
                        value: "bag",
                    },
                    {
                        displayName: "Cart",
                        value: "cart",
                    },
                    {
                        displayName: "Truck",
                        value: "truck",
                    },
                ],
                defaultValue: "cart",
                fieldType: "General",
                sortOrder: 2,
            },
            {
                name: fields.showInWebOnly,
                displayName: "Show in WebOnly",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
        ],
        group: "Common",
    },
};

export default widgetModule;
